import { ChevronDown, ChevronUp } from "lucide-react";
import { useCallback, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * Shared sort + expand primitives for the catalogue + drafts tables.
 * Two sort keys per the issue #218 AC ("Sortable columns: tool name
 * (alphabetical), last updated") plus the two directions; default
 * direction differs by key (text → asc, date → desc) and is owned by
 * the `useCycleSort` hook. Lifted here (rather than inlined in each
 * route) so both surfaces share the same chevron indicator, ARIA
 * affordances, comparator semantics, and expand-state shape.
 */

export type SortKey = "name" | "last_updated";
export type SortDir = "asc" | "desc";

/**
 * Sortable rows must expose `name` and `last_updated` columns. Both
 * `CatalogueRow` and `CatalogueDraftRow` carry these via their
 * `[key: string]: string | number | boolean | null` index signature, so
 * the constraint here matches that posture rather than narrowing to
 * `string` — Sheet values arrive as numbers, booleans, or strings.
 */
export type SortableRow = {
  [key: string]: string | number | boolean | null;
};

/**
 * Column-header button that renders the label with an up/down chevron
 * when the column is the active sort key. Uses `aria-sort` on the
 * parent `<th>` (callers should set it on the `<TableHead>` wrapping
 * this button) — the button itself is plain, not a toggle, so no
 * `aria-pressed`.
 */
export function SortableHeader({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 hover:text-text transition-colors",
        active ? "text-text" : "text-text-muted",
      )}
    >
      {label}
      {active ? (
        dir === "asc" ? (
          <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )
      ) : null}
    </button>
  );
}

/**
 * Compute the `aria-sort` attribute value for a `<TableHead>` cell
 * given the active sort key + direction. Returns `"none"` for inactive
 * columns; `"ascending"` / `"descending"` for the active one. This is
 * the WAI-ARIA contract for sortable columns; screen readers announce
 * the current sort affordance from it.
 */
export function ariaSortFor(
  active: boolean,
  dir: SortDir,
): "ascending" | "descending" | "none" {
  if (!active) return "none";
  return dir === "asc" ? "ascending" : "descending";
}

/**
 * Sort by name (alphabetical, `localeCompare`) or `last_updated`
 * (parsed via `Date.parse`; unparseable / missing values sink to the
 * bottom regardless of direction so they don't dominate either end).
 * Stable within the filter-then-sort pipeline because
 * `Array.prototype.sort` is stable in V8.
 */
export function sortRows<T extends SortableRow>(
  rows: ReadonlyArray<T>,
  sortKey: SortKey,
  sortDir: SortDir,
): T[] {
  const dir = sortDir === "asc" ? 1 : -1;
  const sorted = [...rows];
  if (sortKey === "name") {
    sorted.sort((a, b) => {
      const an = String(a.name ?? "");
      const bn = String(b.name ?? "");
      return an.localeCompare(bn) * dir;
    });
    return sorted;
  }
  sorted.sort((a, b) => {
    const at = parseDateMs(a.last_updated);
    const bt = parseDateMs(b.last_updated);
    if (at === null && bt === null) return 0;
    if (at === null) return 1; // missing values always at bottom
    if (bt === null) return -1;
    return (at - bt) * dir;
  });
  return sorted;
}

function parseDateMs(value: unknown): number | null {
  if (typeof value !== "string" || value.length === 0) return null;
  const n = Date.parse(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Per-row expand state shared by Catalogue + CatalogueDrafts.
 * Returns the current `Set` of expanded row ids plus a stable
 * `toggle(id)` action — re-renders the consumer when the set changes.
 * Lifted out so the two routes don't duplicate the `Set` clone-and-
 * mutate boilerplate.
 */
export function useExpandedIds(): {
  expandedIds: ReadonlySet<string>;
  toggle: (id: string) => void;
} {
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const toggle = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  return { expandedIds, toggle };
}

/**
 * Sort state shared by Catalogue + CatalogueDrafts. Holds the active
 * `(sortKey, sortDir)` pair plus a `cycle(key)` action that:
 *
 *   - flips direction when the caller clicks the already-active key
 *   - switches to `key` with its default direction otherwise
 *
 * Default direction comes from the `defaultDir` argument so each
 * surface can express "name → asc, last_updated → desc" without
 * duplicating the logic.
 */
export function useCycleSort(
  initialKey: SortKey,
  initialDir: SortDir,
  defaultDir: (key: SortKey) => SortDir,
): {
  sortKey: SortKey;
  sortDir: SortDir;
  cycle: (key: SortKey) => void;
} {
  const [sortKey, setSortKey] = useState<SortKey>(initialKey);
  const [sortDir, setSortDir] = useState<SortDir>(initialDir);
  const cycle = useCallback(
    (key: SortKey) => {
      setSortKey((prevKey) => {
        if (prevKey === key) {
          setSortDir((d) => (d === "asc" ? "desc" : "asc"));
          return prevKey;
        }
        setSortDir(defaultDir(key));
        return key;
      });
    },
    [defaultDir],
  );
  return { sortKey, sortDir, cycle };
}

/**
 * Default sort direction for each `SortKey` used by both
 * Catalogue + Drafts: alphabetical for `name`, newest-first for
 * `last_updated`. Exposed as a module-level constant so each surface
 * passes the same reference into `useCycleSort` (stable identity, no
 * dependency-array churn).
 */
export const defaultSortDir = (key: SortKey): SortDir =>
  key === "last_updated" ? "desc" : "asc";
